import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Tooltip } from "@mui/material";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import Papa from "papaparse";
import { get, onValue, ref } from "firebase/database";
import { useParams } from "react-router-dom";

import { database } from "../../firebase";
import MetricCard from "../ui/MetricCard";
import RecordDialog from "../ui/RecordDialog";
import EmptyState from "../ui/EmptyState";
import useSnack from "../ui/useSnack";
import Chart from "../charts/Chart";
import { baseChartOption, lineSeries, metricColor } from "../charts/chartTheme";
import { saveChartImage } from "../../utils/saveChartImage";
import {
  EM_DASH,
  describe,
  formatRelative,
  formatTimestamp,
} from "../../utils/format";

const PROXY = "https://proxyserver-ten.vercel.app/proxy-csv?path=";

const storagePathFromUrl = (url) => {
  const matches = String(url || "").match(/\/o\/(.+)\?/);
  return matches?.[1] ? decodeURIComponent(matches[1]) : null;
};

const ECG = ({ title = "Electrocardiogram", extraActions, menu }) => {
  const { watchId } = useParams();
  const chartRef = useRef(null);
  const { snack, showSnack } = useSnack();

  const [records, setRecords] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [statusDescription, setStatusDescription] = useState("");
  const [autoFollowLatest, setAutoFollowLatest] = useState(true);
  const [trace, setTrace] = useState({ samples: [], min: null, max: null });

  /* --- Records ---------------------------------------------------------- */
  useEffect(() => {
    const watchRef = ref(database, `Health/${watchId}/ecg`);

    const unsubscribe = onValue(watchRef, (snapshot) => {
      if (!snapshot.exists()) {
        setRecords([]);
        setSelectedRecord(null);
        return;
      }

      const all = Object.entries(snapshot.val())
        .map(([ts, record]) => ({ ...record, timestamp: ts }))
        .sort((a, b) => Number(b.timestamp) - Number(a.timestamp));

      setRecords(all);

      const latest = all[0] ?? null;
      setSelectedRecord((previous) => {
        if (!previous || autoFollowLatest) return latest;
        const stillExists = all.some((r) => r.timestamp === previous.timestamp);
        return stillExists ? previous : latest;
      });
    });

    return () => unsubscribe();
  }, [watchId, autoFollowLatest]);

  /* --- Status description ---------------------------------------------- */
  useEffect(() => {
    if (!selectedRecord?.timestamp) {
      setStatusDescription("");
      return;
    }

    get(
      ref(
        database,
        `Health/${watchId}/ecg/${selectedRecord.timestamp}/ecgStatusDescription`
      )
    )
      .then((snapshot) => setStatusDescription(snapshot.exists() ? snapshot.val() : ""))
      .catch((error) => {
        console.error("Error fetching ecgStatusDescription:", error);
        setStatusDescription("");
      });
  }, [watchId, selectedRecord]);

  /* --- Waveform --------------------------------------------------------- */
  const loadTrace = useCallback(async (url) => {
    const path = storagePathFromUrl(url);
    if (!path) return;

    setLoading(true);
    try {
      const response = await fetch(`${PROXY}${encodeURIComponent(path)}`);
      const csvText = await response.text();

      const parsed = Papa.parse(csvText, {
        header: false,
        dynamicTyping: true,
        skipEmptyLines: true,
      });

      const raw = parsed.data.map((row) => row[0]);
      const points = raw.filter(
        (value) => value !== -99999 && Number.isFinite(Number(value))
      );

      // A lone reading in the -900..-800 band is a lead-off artefact, not
      // signal; drop it so it does not flatten the y-scale.
      const artefacts = points.filter((v) => v >= -900 && v <= -800).length;
      const samples = (artefacts === 1
        ? points.filter((v) => !(v >= -900 && v <= -800))
        : points
      ).map(Number);

      setTrace({
        samples,
        min: samples.length ? Math.floor(Math.min(...samples)) : null,
        max: samples.length ? Math.ceil(Math.max(...samples)) : null,
      });
    } catch (error) {
      console.error("Error fetching or parsing ECG CSV:", error);
      setTrace({ samples: [], min: null, max: null });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedRecord?.url) loadTrace(selectedRecord.url);
  }, [selectedRecord, loadTrace]);

  const accent = metricColor("ecg");
  const latestRecord = records[0] ?? null;

  const categories = useMemo(
    () => trace.samples.map((_, index) => index + 1),
    [trace.samples]
  );

  const stats = useMemo(() => describe(trace.samples, 0), [trace.samples]);

  const buildOption = () =>
    baseChartOption({
      categories,
      // A waveform is read by its shape: no smoothing, no markers, no fill —
      // every one of those would misrepresent the signal.
      series: [
        lineSeries({
          name: "ECG",
          data: trace.samples,
          color: metricColor("ecg"),
          area: false,
          smooth: 0,
          width: 1.4,
          showSymbol: false,
        }),
      ],
      xName: "Sample",
      yMin: trace.min !== null ? trace.min - 1 : undefined,
      yMax: trace.max !== null ? trace.max + 1 : undefined,
      tooltipFormatter: (params) => {
        const point = params?.[0];
        if (!point) return "";
        return `Sample ${point.axisValue}<br/><strong>${point.value}</strong>`;
      },
    });

  const handleExport = async () => {
    const result = await saveChartImage(chartRef, watchId, "ecg");
    showSnack(
      result.ok
        ? "Chart saved — find it on the Files page."
        : "Could not export the chart image.",
      result.ok ? "success" : "error"
    );
  };

  return (
    <>
      <MetricCard
        accent={accent}
        icon={<MonitorHeartOutlinedIcon />}
        title={title}
        meta={
          <>
            <span className="wg-chip wg-chip--accent">
              {autoFollowLatest ? "Following latest" : "Manual selection"}
            </span>
            {selectedRecord && (
              <span className="wg-chip">
                {formatTimestamp(selectedRecord.timestamp)}
              </span>
            )}
          </>
        }
        menu={menu}
        actions={
          <>
            {extraActions}
            <Tooltip title="Download a PNG and archive it under Files">
              <span>
                <Button
                  size="small"
                  startIcon={<DownloadOutlinedIcon />}
                  onClick={handleExport}
                  disabled={!trace.samples.length}
                >
                  Export
                </Button>
              </span>
            </Tooltip>
            <Button
              size="small"
              startIcon={<HistoryOutlinedIcon />}
              onClick={() => {
                setAutoFollowLatest(true);
                setSelectedRecord(latestRecord);
              }}
              disabled={!records.length}
            >
              Latest
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setDialogOpen(true)}
              disabled={!records.length}
            >
              Select record
            </Button>
          </>
        }
        note={statusDescription}
        /* Same four-tile shape as the other waveform panels:
           Samples / Amplitude / Average / Recorded. */
        stats={[
          {
            label: "Samples",
            value: trace.samples.length || EM_DASH,
            meta: selectedRecord?.displayName || "ECG session",
          },
          {
            label: "Amplitude",
            value: trace.min !== null ? `${trace.min}–${trace.max}` : EM_DASH,
            meta: "Min to max",
          },
          { label: "Average", value: stats.avg, meta: `${records.length} records stored` },
          {
            label: "Recorded",
            value: selectedRecord ? formatRelative(selectedRecord.timestamp) : EM_DASH,
            small: true,
            meta: selectedRecord ? formatTimestamp(selectedRecord.timestamp) : "No record",
          },
        ]}
        loading={loading}
        empty={
          !loading && !selectedRecord ? (
            <EmptyState
              icon={<MonitorHeartOutlinedIcon />}
              title="No ECG record selected"
              copy="Choose a recording to plot its waveform and export a snapshot."
              action={
                <Button
                  variant="contained"
                  onClick={() => setDialogOpen(true)}
                  disabled={!records.length}
                >
                  Select a record
                </Button>
              }
            />
          ) : null
        }
      >
        <Chart
          buildOption={buildOption}
          chartRef={chartRef}
          height={300}
          deps={[trace.samples]}
        />
      </MetricCard>

      <RecordDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSelect={(record) => {
          setAutoFollowLatest(record.timestamp === latestRecord?.timestamp);
          setSelectedRecord(record);
          setDialogOpen(false);
        }}
        records={records}
        accent={accent}
        title="ECG recordings"
        getTitle={(record) => record.displayName || "ECG recording"}
        getMeta={(record) =>
          `${formatTimestamp(record.timestamp)} · ${formatRelative(record.timestamp)}`
        }
        isSelected={(record) => selectedRecord?.timestamp === record.timestamp}
      />

      {snack}
    </>
  );
};

export default ECG;
